import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Icon from './Icon';
import Avatar from './Avatar';
import { useModal } from '../context/ModalContext';
import { getUserRole } from '../utils/rolePermissions';
import '../styles/Modal.css';
import '../styles/CommentModal.css';

const CommentModal = ({
  isOpen,
  onClose,
  itemId,
  itemType,
  docId,
  comments,
  user,
  onCommentAdded,
  onCommentDeleted,
  isProxyMode = false
}) => {
  const [commentText, setCommentText] = useState('');
  const [localComments, setLocalComments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const modal = useModal();
  const commentsListRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && comments) {
      setLocalComments(comments);
    }
  }, [isOpen, comments]);

  if (!isOpen) return null;

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const buildEndpoint = (action, commentId = null) => {
    switch (itemType) {
      case 'content-draft':
        if (action === 'add') {
          return `/api/content-drafts/${itemId}/comment`;
        } else if (action === 'delete' && commentId) {
          return `/api/content-drafts/${itemId}/comment/${commentId}`;
        }
        break;
      case 'idea':
        if (action === 'add') {
          return `/api/ideas/${docId}/idea/${itemId}/comment`;
        } else if (action === 'delete' && commentId) {
          return `/api/ideas/${docId}/idea/${itemId}/comment/${commentId}`;
        }
        break;
      case 'brand-voice-completion':
        if (action === 'add') {
          return `/api/brand-voice/completion/${itemId}/comment`;
        } else if (action === 'delete' && commentId) {
          return `/api/brand-voice/completion/${itemId}/comment/${commentId}`;
        }
        break;
      case 'meeting':
        if (action === 'add') {
          return `/api/livekit/meetings/${itemId}/comment`;
        } else if (action === 'delete' && commentId) {
          return `/api/livekit/meetings/${itemId}/comment/${commentId}`;
        }
        break;
      default:
        return '';
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const endpoint = buildEndpoint('add');
      const payload = { text: commentText };

      const response = await axios.post(endpoint, payload, { withCredentials: true });

      if (response.data.comment) {
        const newComment = response.data.comment;
        const updatedComments = [...localComments, newComment];
        setLocalComments(updatedComments);
        setCommentText('');
        modal.success('Comment added successfully');

        // Auto-scroll to bottom to show the new comment
        setTimeout(() => {
          if (commentsListRef.current) {
            commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
          }
        }, 100);

        if (onCommentAdded) {
          onCommentAdded(updatedComments);
        }
      }
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const result = await modal.confirm(
      'Are you sure you want to delete this comment?',
      'Delete Comment'
    );
    if (!result) return;

    setIsDeletingId(commentId);
    try {
      const endpoint = buildEndpoint('delete', commentId);
      await axios.delete(endpoint, { withCredentials: true });

      const updatedComments = localComments.filter(c => (c._id || c.id) !== commentId);
      setLocalComments(updatedComments);
      modal.success('Comment deleted successfully');

      if (onCommentDeleted) {
        onCommentDeleted(updatedComments);
      }
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to delete comment');
    } finally {
      setIsDeletingId(null);
    }
  };

  const canDeleteComment = (comment) => {
    const commentUserId = String(comment.user_id || '');
    const currentUserId = String(user._id || '');
    const commentUserEmail = String(comment.user_email || '').toLowerCase();
    const currentUserEmail = String(user.email || '').toLowerCase();

    const isOwner = (commentUserId && commentUserId === currentUserId) ||
                    (commentUserEmail && commentUserEmail === currentUserEmail);
    const isSuperAdmin = getUserRole(user) === 'super_admin';
    const hasValidId = !!(comment._id || comment.id);

    return (isOwner || isSuperAdmin || isProxyMode) && hasValidId;
  };

  const handleCancel = () => {
    setCommentText('');
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-container modal-medium">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            Comments {localComments.length > 0 && `(${localComments.length})`}
          </h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close" />
        </div>

        {/* Body */}
        <div className="modal-body comment-modal-body">
          {/* Comments List */}
          <div className="comment-list-section" ref={commentsListRef}>
            {localComments.length === 0 ? (
              <div className="comment-empty-state">No comments yet</div>
            ) : (
              <div className="comment-list">
                {localComments.map((comment, index) => {
                  const commentId = comment._id || comment.id;
                  const commentText = comment.text || comment.comment_text;
                  const canDelete = canDeleteComment(comment);
                  const isDeleting = isDeletingId === commentId;

                  return (
                    <div key={commentId || index} className="comment-item">
                      <div className="comment-item-header">
                        <div className="comment-user-info">
                          <Avatar
                            user={{
                              _id: comment.user_id,
                              agency_id: comment.agency_id,
                              name: comment.user_name || comment.userName || 'User'
                            }}
                            size="sm"
                            className="comment-profile-pic"
                          />
                          <strong className="comment-user-name">
                            {comment.user_name || comment.userName || 'User'}
                          </strong>
                        </div>
                        <div className="comment-header-right">
                          <span className="comment-timestamp">
                            {formatCommentTimestamp(comment.timestamp || comment.created_at)}
                          </span>
                          {canDelete && (
                            <button
                              className="comment-delete-btn"
                              onClick={() => handleDeleteComment(commentId)}
                              disabled={isDeleting}
                              title="Delete comment"
                            >
                              <Icon name="cancel" size="0.875rem" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="comment-text">{commentText}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Comment Input Section */}
          <div className="comment-input-section">
            <div className="comment-input-header">
              <div className="comment-user-info">
                <Avatar user={user} size="sm" className="comment-profile-pic" />
                <span className="comment-user-name">{user.name || 'User'}</span>
              </div>
            </div>
            <textarea
              className="comment-textarea"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment"
              rows="3"
              disabled={isSubmitting}
            />
            <div className="comment-actions">
              <button
                className="modal-btn modal-btn-cancel"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className={`modal-btn modal-btn-confirm ${!commentText.trim() || isSubmitting ? 'disabled' : ''}`}
                onClick={handleAddComment}
                disabled={!commentText.trim() || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommentModal;
